export interface DispatcherWebRtcCallbacks {
  onIceCandidate: (candidate: RTCIceCandidateInit) => void;
  onRemoteStream: (stream: MediaStream) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
}

export class DispatcherWebRtcService {
  private peerConnection: RTCPeerConnection | null = null;
  private localAudioStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private callbacks?: DispatcherWebRtcCallbacks;
  private pendingCandidates: RTCIceCandidateInit[] = [];

  constructor() {}

  public async initializeMicrophone(): Promise<MediaStream | null> {
    try {
      this.localAudioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      });
      return this.localAudioStream;
    } catch (e) {
      console.warn('[MIC] Micrófono del despachador no disponible o bloqueado:', e);
      return null;
    }
  }

  public setupPeerConnection(
    iceServers: RTCIceServer[],
    callbacks: DispatcherWebRtcCallbacks
  ): RTCPeerConnection {
    this.callbacks = callbacks;
    this.pendingCandidates = [];

    const defaultIceServers: RTCIceServer[] = [
      { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302'] },
      { urls: 'stun:stun.cloudflare.com:3478' }
    ];

    this.peerConnection = new RTCPeerConnection({
      iceServers: iceServers.length > 0 ? iceServers : defaultIceServers,
      bundlePolicy: 'max-bundle'
    });

    // Agregar audio del despachador si está disponible
    if (this.localAudioStream) {
      this.localAudioStream.getAudioTracks().forEach((track) => {
        if (this.localAudioStream && this.peerConnection) {
          this.peerConnection.addTrack(track, this.localAudioStream);
        }
      });
    }

    this.peerConnection.onicecandidate = (event: RTCPeerConnectionIceEvent) => {
      if (event.candidate && this.callbacks?.onIceCandidate) {
        this.callbacks.onIceCandidate(event.candidate.toJSON());
      }
    };

    this.peerConnection.ontrack = (event: RTCTrackEvent) => {
      if (event.streams && event.streams[0]) {
        this.remoteStream = event.streams[0];
        this.callbacks?.onRemoteStream(this.remoteStream);
      } else if (event.track) {
        this.remoteStream = new MediaStream([event.track]);
        this.callbacks?.onRemoteStream(this.remoteStream);
      }
    };

    this.peerConnection.onconnectionstatechange = () => {
      if (this.peerConnection && this.callbacks?.onConnectionStateChange) {
        this.callbacks.onConnectionStateChange(this.peerConnection.connectionState);
      }
    };

    return this.peerConnection;
  }

  public async handleOfferAndCreateAnswer(offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) throw new Error('PeerConnection no inicializado');
    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));

    // Despachar candidatos ICE que hayan llegado mientras se negociaba el SDP
    if (this.pendingCandidates.length > 0) {
      for (const cand of this.pendingCandidates) {
        try {
          await this.peerConnection.addIceCandidate(new RTCIceCandidate(cand));
        } catch (e) {
          console.warn('[WEBRTC CANDIDATE ERROR]', e);
        }
      }
      this.pendingCandidates = [];
    }

    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);
    return answer;
  }

  public async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) return;
    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));

    if (this.pendingCandidates.length > 0) {
      for (const cand of this.pendingCandidates) {
        try {
          await this.peerConnection.addIceCandidate(new RTCIceCandidate(cand));
        } catch (e) {
          console.warn('[WEBRTC CANDIDATE ERROR]', e);
        }
      }
      this.pendingCandidates = [];
    }
  }

  public async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!candidate || !candidate.candidate) return;
    if (!this.peerConnection || !this.peerConnection.remoteDescription) {
      // Si el SDP remoto aún no se ha procesado, almacenar en cola para no perderlo
      this.pendingCandidates.push(candidate);
      return;
    }
    try {
      await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (e) {
      console.warn('[WEBRTC] Error agregando candidato ICE en despachador:', e);
    }
  }

  public captureSnapshot(videoElement: HTMLVideoElement): string | null {
    if (!videoElement || videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.92);
  }

  public dispose(): void {
    if (this.localAudioStream) {
      this.localAudioStream.getTracks().forEach((t) => t.stop());
      this.localAudioStream = null;
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.remoteStream = null;
  }
}
